import { RectGrid } from './RectGrid'
import { AdminObjectGrid } from './AdminObjectGrid'
import { CircleGrid } from './CircleGrid'
import { TextGrid } from './TextGrid'
import { AdminImageGrid } from './AdminImageGrid'
import { PromptTextboxGrid } from './PromptTextboxGrid'
import { TriangleGrid } from './TriangleGrid'
import { CanvasGrid } from './CanvasGrid'
import { UndoButton } from './ui/UndoButton'
import { RedoButton } from './ui/RedoButton'
import Sheet from '@mui/joy/Sheet'
import Box from '@mui/joy/Box'

const UndoRedo = ({ canvas }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <UndoButton
                    onClick={canvas.undo.bind(canvas)}
                    disabled={!canvas.canUndo()}
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <RedoButton
                    onClick={canvas.redo.bind(canvas)}
                    disabled={!canvas.canRedo()}
                />
            </Box>
        </Box>
    )
}

export const AdminObjectPanel = ({ canvas, obj, onDelete, onChange }) => {
    const handleChange = () => {
        onChange(
            canvas.toDataURL({
                format: 'png',
                multiplier: 2,
            })
        )
    }

    const handleBorderWidthChange = ({ obj, value }) => {
        obj.set('strokeWidth', value)
        canvas.renderAll()
        handleChange()
    }

    const handleOpacityChange = ({ obj, value }) => {
        obj.set('opacity', value)
        canvas.renderAll()
        handleChange()
    }

    const handleTextAlignChange = ({ obj, value }) => {
        obj.set('textAlign', value)
        canvas.renderAll()
        handleChange()
    }

    const handleFontSizeChange = ({ obj, value }) => {
        obj.set('fontSize', value)
        canvas.renderAll()
        handleChange()
    }

    const handlePromptChange = ({ obj, value }) => {
        obj.set('prompt', value)
        handleChange()
    }

    const handleBorderRadiusChange = ({ obj, value }) => {
        obj.set({ rx: value, ry: value })
        canvas.renderAll()
        handleChange()
    }

    const handleFillColorChange = ({ obj, value }) => {
        obj.set({ fill: value })
        canvas.renderAll()
        handleChange()
    }

    const handleBorderColorChange = ({ obj, value }) => {
        obj.set({ stroke: value })
        canvas.renderAll()
        handleChange()
    }

    const handleCanvasColorChange = ({ canvas, value }) => {
        canvas.setBackgroundColor(value)
        canvas.renderAll()
        handleChange()
    }

    const handleFontChange = ({ obj, value }) => {
        obj.set({ fontFamily: value })
        canvas.renderAll()
        handleChange()
    }

    const handleFontWeightChange = ({ obj, value }) => {
        console.log('font weight change')
        obj.set({ fontWeight: value })
        canvas.renderAll()
        handleChange()
    }

    const handleLayerUp = (obj) => {
        obj.bringForward()
        canvas.renderAll()
        handleChange()
    }

    const handleLayerDown = (obj) => {
        obj.sendBackwards()
        canvas.renderAll()
        handleChange()
    }

    const handleTextChange = (obj) => {
        canvas.renderAll()
        handleChange()
    }

    const handleLockToggle = (obj) => {
        let isLocked = !obj.lockScalingX
        obj.set({
            lockScalingX: isLocked,
            lockScalingY: isLocked,
            lockRotation: isLocked,
            lockMovementX: isLocked,
            lockMovementY: isLocked,
        })
    }

    const replaceImage = (e) => {
        var file = e.target.files[0]
        console.log(file)
        var reader = new FileReader()
        reader.addEventListener(
            'load',
            () => {
                // convert image file to base64 string
                console.log('result!!!!!')
                console.log(reader.result)
                obj.setSrc(reader.result, (oImg) => {
                    var scale = 200 / oImg.width
                    oImg.set({ scaleX: scale, scaleY: scale })
                    canvas.renderAll()
                })
            },
            false
        )
        reader.readAsDataURL(file)
    }

    const replaceTemplateImage = (e) => {
        var file = e.target.files[0]
        console.log(file)
        var reader = new FileReader()
        reader.addEventListener(
            'load',
            () => {
                // convert image file to base64 string
                console.log('result!!!!!')
                console.log(reader.result)
                obj.setSrc(reader.result, () => {
                    canvas.renderAll()
                })
            },
            false
        )
        reader.readAsDataURL(file)
    }

    const createGrid = (child, obj) => (
        <AdminObjectGrid
            key={obj.id}
            onOpacityChange={({ obj, value }) =>
                handleOpacityChange({ obj, value })
            }
            onFillColorChange={handleFillColorChange}
            obj={obj}
            onLayerUp={handleLayerUp}
            onLayerDown={handleLayerDown}
            onDelete={handleDelete}
            onLockToggle={handleLockToggle}
            onBorderWidthChange={handleBorderWidthChange}
            onBorderColorChange={handleBorderColorChange}
        >
            {child}
        </AdminObjectGrid>
    )

    const createRectGrid = (obj) => {
        return createGrid(
            <RectGrid
                onBorderRadiusChange={handleBorderRadiusChange}
                rect={obj}
            />,
            obj
        )
    }

    const createCircleGrid = (obj) => {
        return createGrid(<CircleGrid />, obj)
    }

    const createTextGrid = (obj) => {
        return createGrid(
            <TextGrid
                onFontChange={handleFontChange}
                onFontSizeChange={handleFontSizeChange}
                onTextChange={handleTextChange}
                onWeightChange={handleFontWeightChange}
                onTextAlignChange={handleTextAlignChange}
                textObj={obj}
            />,
            obj
        )
    }

    const createPromptTextboxGrid = (obj) => {
        return createGrid(
            <PromptTextboxGrid
                onFontChange={handleFontChange}
                onFontSizeChange={handleFontSizeChange}
                onTextChange={handleTextChange}
                onWeightChange={handleFontWeightChange}
                onTextAlignChange={handleTextAlignChange}
                onPromptChange={handlePromptChange}
                textObj={obj}
            />,
            obj
        )
    }

    const createTriangleGrid = (obj) => {
        return createGrid(<TriangleGrid />, obj)
    }

    const createImageGrid = (obj) => {
        return createGrid(
            <AdminImageGrid img={obj} onFileChange={replaceImage} />,
            obj
        )
    }

    const createTemplateImageGrid = (obj) => {
        return createGrid(
            <AdminImageGrid img={obj} onFileChange={replaceTemplateImage} />,
            obj
        )
    }

    const PANEL_DICT = {
        rect: createRectGrid,
        'template-i-text': createTextGrid,
        'i-text': createTextGrid,
        textbox: createTextGrid,
        'template-textbox': createTextGrid,
        circle: createCircleGrid,
        triangle: createTriangleGrid,
        image: createImageGrid,
        'template-image': createTemplateImageGrid,
        'prompt-textbox': createPromptTextboxGrid,
    }

    const handleDelete = ({ obj }) => {
        canvas.remove(obj)
        canvas.renderAll()
        onDelete()
    }

    return (
        <Sheet
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                borderWidth: '0 0 1px 0',
                p: 2,
                gap: 2,
            }}
            color="neutral"
        >
            {canvas && <UndoRedo canvas={canvas} />}
            {obj ? (
                PANEL_DICT[obj.type](obj)
            ) : (
                <CanvasGrid
                    canvas={canvas}
                    onColorChange={handleCanvasColorChange}
                />
            )}
        </Sheet>
    )
}
